import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenantView } from '../contexts/TenantViewContext';
import LoadingSpinner from './LoadingSpinner';
import PreAuth from '../pages/PreAuth';
import Login from '../pages/Login';
import Register from '../pages/Register';
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';
import AcceptInvitation from '../pages/AcceptInvitation';
import OnboardingSlides from '../pages/OnboardingSlides';
import Dashboard from '../pages/Dashboard';
import Notices from '../pages/Notices';
const Bills = lazy(() => import('../pages/Bills'));
import TopUp from '../pages/TopUp';
import PaymentSuccess from '../pages/PaymentSuccess';
import Settings from '../pages/Settings';
import Notifications from '../pages/settings/Notifications';
import Security from '../pages/settings/Security';
import Address from '../pages/settings/Address';
import DeviceManagement from '../pages/settings/DeviceManagement';
import PrivacyData from '../pages/settings/PrivacyData';
import Privacy from '../pages/Privacy';
import Demo from '../pages/Demo';
import NotFound from '../pages/NotFound';
import Forbidden from '../pages/Forbidden';
import PublicRecharge from '../pages/PublicRecharge';
const Contracts = lazy(() => import('../pages/Contracts'));
const SignContract = lazy(() => import('../pages/user/SignContract'));
import Chatbot from '../pages/Chatbot';
import ConsumptionEvolution from '../pages/ConsumptionEvolution';
import Documentation from '../pages/Documentation';
import UsageGuide from '../pages/UsageGuide';
import ApplianceInsights from '../pages/ApplianceInsights';
import ContactSupport from '../pages/ContactSupport';
import ContractVerification from '../pages/ContractVerification';
import BillVerification from '../pages/BillVerification';
import TopUpVerification from '../pages/TopUpVerification';
import {
  AdminDashboard,
  Buildings,
  BuildingPricing,
  ContractManagement,
  CreateOrganization,
  NotifycClient,
  SmartMeters,
  Manufacturers,
  UnitManagement,
  Gateways,
  SystemReports,
  UserManagement,
  PDFTemplates,
  ClickPesaTesting,
  SystemTests,
  AccessControl,
  TopUpHistory,
  Reports,
  ActivityLogs,
  FinancialLogs,
  SuperAdminBillReports,
  RoleAssignments,
  MeterOperations,
  SupportTickets,
  SprintBoard,
  LazyAdminWrapper
} from './Admin/LazyAdminPages';

import TemperatureHumidity from '../pages/TemperatureHumidity';

const PlatformAdminManagement = lazy(() => import("@/pages/admin/PlatformAdminManagement"));
const SuperAdminSettings = lazy(() => import("@/pages/admin/SuperAdminSettings"));
const SuperAdminTools = lazy(() => import("@/pages/admin/SuperAdminTools"));

// Owner pages
const OwnerDashboard = lazy(() => import("@/pages/owner/OwnerDashboard"));
const OwnerUnits = lazy(() => import("@/pages/owner/OwnerUnits"));
const OwnerUnitDetails = lazy(() => import("@/pages/owner/OwnerUnitDetails"));
const OwnerAssignRoles = lazy(() => import("@/pages/owner/OwnerAssignRoles"));
const OwnerTopUp = lazy(() => import("@/pages/owner/OwnerTopUp"));
const OwnerTopUpHistory = lazy(() => import("@/pages/owner/OwnerTopUpHistory"));

// Concierge pages
const ConciergeDashboard = lazy(() => import("@/pages/concierge/ConciergeDashboard"));
const ConciergeApartments = lazy(() => import("@/pages/concierge/ConciergeApartments"));
const ConciergeApartmentDetail = lazy(() => import("@/pages/concierge/ConciergeApartmentDetail"));
const ConciergeTopUp = lazy(() => import("@/pages/concierge/ConciergeTopUp"));
const ConciergeTopUpHistory = lazy(() => import("@/pages/concierge/ConciergeTopUpHistory"));

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading, isAdmin, isPlatformAdmin, isOwner, isConcierge } = useAuth();
  const { inTenantView } = useTenantView();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/preauth" replace />;
  }

  // Allow admins/superadmins to access user routes when they've explicitly switched via the ViewToggle
  if (!inTenantView) {
    // Platform admins should see admin dashboard (they're super-admins)
    if (isPlatformAdmin) {
      return <Navigate to="/admin/dashboard" replace />;
    }

    // Organization admins should also see admin dashboard
    if (isAdmin()) {
      return <Navigate to="/admin/dashboard" replace />;
    }
  }

  // Owners should see owner dashboard (skip when admin has switched to tenant view)
  // Exception: redirect /user/* paths to their /owner/* equivalents instead of the dashboard
  if (!inTenantView && isOwner()) {
    const ownerEquivalent = location.pathname.replace(/^\/user\//, '/owner/');
    if (ownerEquivalent !== location.pathname) {
      return <Navigate to={ownerEquivalent + location.search} replace />;
    }
    return <Navigate to="/owner/dashboard" replace />;
  }

  // Concierges should see concierge dashboard (skip when admin has switched to tenant view)
  if (!inTenantView && isConcierge()) {
    return <Navigate to="/concierge/dashboard" replace />;
  }

  return children;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading, isAdmin, isPlatformAdmin } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/preauth" replace />;
  }

  // Allow access if user is platform admin OR organization admin
  // Platform admins have access to ALL admin routes
  if (!isAdmin() && !isPlatformAdmin) {
    return <Navigate to="/user/dashboard" replace />;
  }

  return children;
};

// Route component for platform admin only pages
const PlatformAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading, isPlatformAdmin } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/preauth" replace />;
  }

  // Only platform admins can access these routes
  if (!isPlatformAdmin) {
    return <Navigate to="/403" replace />;
  }

  return children;
};

// Route component for owner only pages
const OwnerRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading, isOwner, isAdmin, isPlatformAdmin } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/preauth" replace />;
  }

  // Admins and platform admins can also access owner routes (for management)
  if (!isOwner() && !isAdmin() && !isPlatformAdmin) {
    return <Navigate to="/user/dashboard" replace />;
  }

  return children;
};

// Route component for concierge pages
const ConciergeRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading, isConcierge, isAdmin, isPlatformAdmin } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/preauth" replace />;
  }

  // Admins and platform admins can also access concierge routes (for management)
  if (!isConcierge() && !isAdmin() && !isPlatformAdmin) {
    return <Navigate to="/user/dashboard" replace />;
  }

  return children;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading, isAdmin, isPlatformAdmin, isOwner, isConcierge, isValidatingOrg } = useAuth();
  const { inTenantView } = useTenantView();
  const location = window.location;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Allow public recharge page even if logged in
  if (location.pathname.startsWith('/recharge/')) {
    return <>{children}</>;
  }

  // Don't redirect while validating organization membership
  // This prevents redirect to dashboard before org check completes
  if (isValidatingOrg) {
    return <>{children}</>;
  }

  if (user) {
    // Platform admins and organization admins go to admin dashboard (unless in tenant view)
    const shouldGoToAdmin = !inTenantView && (isPlatformAdmin || isAdmin());
    if (shouldGoToAdmin) {
      return <Navigate to="/admin/dashboard" replace />;
    }
    // Owners go to owner dashboard (skip when in tenant view — admin browsing as a tenant)
    if (!inTenantView && isOwner()) {
      return <Navigate to="/owner/dashboard" replace />;
    }
    // Concierges go to concierge dashboard (skip when in tenant view)
    if (!inTenantView && isConcierge()) {
      return <Navigate to="/concierge/dashboard" replace />;
    }
    return <Navigate to="/user/dashboard" replace />;
  }

  return children;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Navigate to="/preauth" replace />} />
        <Route path="/preauth" element={<PublicRoute><PreAuth /></PublicRoute>} />
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
    <Route path="/recharge/:token" element={<PublicRecharge />} />
    <Route path="/onboarding" element={<OnboardingSlides />} />
    
    {/* User Routes */}
    <Route path="/user/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
    <Route path="/user/notices" element={<ProtectedRoute><Notices /></ProtectedRoute>} />
    <Route path="/user/bills" element={<ProtectedRoute><Suspense fallback={<LoadingSpinner size="lg" />}><Bills /></Suspense></ProtectedRoute>} />
    <Route path="/user/contracts" element={<ProtectedRoute><Suspense fallback={<LoadingSpinner size="lg" />}><Contracts /></Suspense></ProtectedRoute>} />
    <Route path="/user/sign-contract/:contractId" element={<ProtectedRoute><Suspense fallback={<LoadingSpinner size="lg" />}><SignContract /></Suspense></ProtectedRoute>} />
    <Route path="/user/chatbot" element={<ProtectedRoute><Chatbot /></ProtectedRoute>} />
    <Route path="/user/documentation" element={<ProtectedRoute><Documentation /></ProtectedRoute>} />
    <Route path="/user/consumption-evolution" element={<ProtectedRoute><ConsumptionEvolution /></ProtectedRoute>} />
    <Route path="/user/appliance-insights" element={<ProtectedRoute><ApplianceInsights /></ProtectedRoute>} />
    <Route path="/user/contact-support" element={<ProtectedRoute><ContactSupport /></ProtectedRoute>} />
    <Route path="/user/topup" element={<ProtectedRoute><TopUp /></ProtectedRoute>} />
    <Route path="/user/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
    <Route path="/user/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
    <Route path="/user/settings/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
    <Route path="/user/documentation" element={<Navigate to="/documentation" replace />} />
    
    <Route path="/user/settings/security" element={<ProtectedRoute><Security /></ProtectedRoute>} />
    <Route path="/user/settings/address" element={<ProtectedRoute><Address /></ProtectedRoute>} />
    <Route path="/user/settings/devices" element={<ProtectedRoute><DeviceManagement /></ProtectedRoute>} />
    <Route path="/user/settings/privacy-data" element={<ProtectedRoute><PrivacyData /></ProtectedRoute>} />
    <Route path="/user/temperature-humidity" element={<ProtectedRoute><TemperatureHumidity /></ProtectedRoute>} />
    
    {/* Public Pages */}
    <Route path="/privacy" element={<Privacy />} />
    <Route path="/documentation" element={<Documentation />} />
    <Route path="/usage-guide" element={<UsageGuide />} />
    <Route path="/demo" element={<Demo />} />
    
    {/* Error Pages */}
    <Route path="/403" element={<Forbidden />} />
    
    {/* Admin Routes */}
    <Route path="/admin/dashboard" element={<AdminRoute><LazyAdminWrapper><AdminDashboard /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/create-organization" element={<AdminRoute><LazyAdminWrapper><CreateOrganization /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/users" element={<AdminRoute><LazyAdminWrapper><UserManagement /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/buildings" element={<AdminRoute><LazyAdminWrapper><Buildings /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/building-pricing" element={<AdminRoute><LazyAdminWrapper><BuildingPricing /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/smart-meters" element={<AdminRoute><LazyAdminWrapper><SmartMeters /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/manufacturers" element={<AdminRoute><LazyAdminWrapper><Manufacturers /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/units" element={<AdminRoute><LazyAdminWrapper><UnitManagement /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/gateways" element={<AdminRoute><LazyAdminWrapper><Gateways /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/contracts" element={<AdminRoute><LazyAdminWrapper><ContractManagement /></LazyAdminWrapper></AdminRoute>} />
    {/* Old SystemReports route - replaced by new Reports page */}
    <Route path="/admin/notifyc_client" element={<AdminRoute><LazyAdminWrapper><NotifycClient /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/pdf-templates" element={<AdminRoute><LazyAdminWrapper><PDFTemplates /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/testing-functions" element={<AdminRoute><LazyAdminWrapper><ClickPesaTesting /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/system-tests" element={<AdminRoute><LazyAdminWrapper><SystemTests /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/access-control" element={<AdminRoute><LazyAdminWrapper><AccessControl /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/topup-history" element={<AdminRoute><LazyAdminWrapper><TopUpHistory /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/reports" element={<AdminRoute><LazyAdminWrapper><Reports /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/financial-logs" element={<AdminRoute><LazyAdminWrapper><FinancialLogs scope="org" /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/role-assignments" element={<AdminRoute><LazyAdminWrapper><RoleAssignments /></LazyAdminWrapper></AdminRoute>} />
    <Route path="/admin/documentation" element={<Navigate to="/documentation" replace />} />

    {/* Owner Routes */}
    <Route path="/owner/dashboard" element={<OwnerRoute><Suspense fallback={<LoadingSpinner size="lg" />}><OwnerDashboard /></Suspense></OwnerRoute>} />
    <Route path="/owner/units" element={<OwnerRoute><Suspense fallback={<LoadingSpinner size="lg" />}><OwnerUnits /></Suspense></OwnerRoute>} />
    <Route path="/owner/units/:unitId" element={<OwnerRoute><Suspense fallback={<LoadingSpinner size="lg" />}><OwnerUnitDetails /></Suspense></OwnerRoute>} />
    <Route path="/owner/assign-roles" element={<OwnerRoute><Suspense fallback={<LoadingSpinner size="lg" />}><OwnerAssignRoles /></Suspense></OwnerRoute>} />
    <Route path="/owner/bills" element={<OwnerRoute><Suspense fallback={<LoadingSpinner size="lg" />}><Bills /></Suspense></OwnerRoute>} />
    <Route path="/owner/topup" element={<OwnerRoute><OwnerTopUp /></OwnerRoute>} />
    <Route path="/owner/topup-history" element={<OwnerRoute><Suspense fallback={<LoadingSpinner size="lg" />}><OwnerTopUpHistory /></Suspense></OwnerRoute>} />
    <Route path="/owner/contracts" element={<OwnerRoute><Suspense fallback={<LoadingSpinner size="lg" />}><Contracts /></Suspense></OwnerRoute>} />
    <Route path="/owner/sign-contract/:contractId" element={<OwnerRoute><Suspense fallback={<LoadingSpinner size="lg" />}><SignContract /></Suspense></OwnerRoute>} />
    <Route path="/owner/settings" element={<OwnerRoute><Settings /></OwnerRoute>} />

    {/* Concierge Routes */}
    <Route path="/concierge/dashboard" element={<ConciergeRoute><Suspense fallback={<LoadingSpinner size="lg" />}><ConciergeDashboard /></Suspense></ConciergeRoute>} />
    <Route path="/concierge/apartments" element={<ConciergeRoute><Suspense fallback={<LoadingSpinner size="lg" />}><ConciergeApartments /></Suspense></ConciergeRoute>} />
    <Route path="/concierge/apartments/:apartmentId" element={<ConciergeRoute><Suspense fallback={<LoadingSpinner size="lg" />}><ConciergeApartmentDetail /></Suspense></ConciergeRoute>} />
    <Route path="/concierge/topup" element={<ConciergeRoute><Suspense fallback={<LoadingSpinner size="lg" />}><ConciergeTopUp /></Suspense></ConciergeRoute>} />
    <Route path="/concierge/topup-history" element={<ConciergeRoute><Suspense fallback={<LoadingSpinner size="lg" />}><ConciergeTopUpHistory /></Suspense></ConciergeRoute>} />
    <Route path="/concierge/settings" element={<ConciergeRoute><Settings /></ConciergeRoute>} />

    {/* Super Admin Routes - only accessible by super admins (platform admins) */}
    <Route path="/superadmin" element={<PlatformAdminRoute><Suspense fallback={<LoadingSpinner size="lg" />}><PlatformAdminManagement /></Suspense></PlatformAdminRoute>} />
    <Route path="/superadmin/activity-logs" element={<PlatformAdminRoute><LazyAdminWrapper><ActivityLogs /></LazyAdminWrapper></PlatformAdminRoute>} />
    <Route path="/superadmin/financial-logs" element={<PlatformAdminRoute><LazyAdminWrapper><FinancialLogs /></LazyAdminWrapper></PlatformAdminRoute>} />
    <Route path="/superadmin/bill-reports" element={<PlatformAdminRoute><LazyAdminWrapper><SuperAdminBillReports /></LazyAdminWrapper></PlatformAdminRoute>} />
    <Route path="/superadmin/meter-operations" element={<PlatformAdminRoute><LazyAdminWrapper><MeterOperations /></LazyAdminWrapper></PlatformAdminRoute>} />
    <Route path="/superadmin/support-tickets" element={<PlatformAdminRoute><LazyAdminWrapper><SupportTickets /></LazyAdminWrapper></PlatformAdminRoute>} />
    <Route path="/superadmin/sprint-board" element={<PlatformAdminRoute><LazyAdminWrapper><SprintBoard /></LazyAdminWrapper></PlatformAdminRoute>} />
    <Route path="/superadmin/tools" element={<PlatformAdminRoute><LazyAdminWrapper><SuperAdminTools /></LazyAdminWrapper></PlatformAdminRoute>} />
    <Route path="/superadmin/settings" element={<PlatformAdminRoute><LazyAdminWrapper><SuperAdminSettings /></LazyAdminWrapper></PlatformAdminRoute>} />
    {/* Redirect old routes to combined financial logs */}
    <Route path="/superadmin/payment-logs" element={<Navigate to="/superadmin/financial-logs" replace />} />
    <Route path="/superadmin/pending-reloads" element={<Navigate to="/superadmin/financial-logs" replace />} />
    {/* Legacy routes for backward compatibility */}
    <Route path="/admin/super-admins" element={<Navigate to="/superadmin" replace />} />
    <Route path="/admin/platform-admins" element={<Navigate to="/superadmin" replace />} />
    <Route path="/platform-admin" element={<PlatformAdminRoute><Navigate to="/admin/dashboard" replace /></PlatformAdminRoute>} />
    <Route path="/platform-admin/*" element={<PlatformAdminRoute><Navigate to="/admin/dashboard" replace /></PlatformAdminRoute>} />

    {/* Legacy routes for backward compatibility */}
    <Route path="/dashboard" element={<Navigate to="/user/dashboard" replace />} />
    <Route path="/notices" element={<Navigate to="/user/notices" replace />} />
    <Route path="/bills" element={<Navigate to="/user/bills" replace />} />
    <Route path="/contracts" element={<Navigate to="/user/contracts" replace />} />
    <Route path="/chatbot" element={<Navigate to="/user/chatbot" replace />} />
    <Route path="/consumption-evolution" element={<Navigate to="/user/consumption-evolution" replace />} />
    <Route path="/info" element={<Navigate to="/user/contact-support" replace />} />
    <Route path="/contacts" element={<Navigate to="/user/contact-support" replace />} />
    <Route path="/topup" element={<Navigate to="/user/topup" replace />} />
    <Route path="/profile" element={<Navigate to="/user/settings" replace />} />
    <Route path="/settings" element={<Navigate to="/user/settings" replace />} />
    <Route path="/settings/notifications" element={<Navigate to="/user/settings/notifications" replace />} />
    
    <Route path="/settings/security" element={<Navigate to="/user/settings/security" replace />} />
    <Route path="/settings/address" element={<Navigate to="/user/settings/address" replace />} />
    <Route path="/settings/devices" element={<Navigate to="/user/settings/devices" replace />} />
    <Route path="/settings/privacy-data" element={<Navigate to="/user/settings/privacy-data" replace />} />
    
    {/* Verification pages */}
    <Route path="/contracts/verify" element={<ContractVerification />} />
    <Route path="/bills/verify" element={<BillVerification />} />
    <Route path="/topup/verify" element={<TopUpVerification />} />

    <Route path="/temperature-humidity" element={<Navigate to="/user/temperature-humidity" replace />} />
    <Route path="/piloting" element={<Navigate to="/admin/dashboard" replace />} />
    <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
    
    <Route path="*" element={<NotFound />} />
  </Routes>
);

export default AppRoutes;
